import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import MapView from "@/components/MapView";
import { Navigation, Star, MapPin, Clock, ArrowLeft, Phone, Info, Truck, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const DAYS = ["Pazartesi", "Salı", "Çarşamba", "Perşembe", "Cuma", "Cumartesi", "Pazar"];

export default function BusinessDetail() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [b, setB] = useState(null);
  const [cats, setCats] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");

  const load = useCallback(async () => {
    const { data } = await api.get(`/businesses/${slug}`);
    setB(data);
    api.get("/categories").then(({ data }) => setCats(data));
    api.get(`/businesses/${data.id}/vehicles`).then(({ data }) => setVehicles(data));
    api.get("/reviews", { params: { business_id: data.id } }).then(({ data }) => setReviews(data));
  }, [slug]);

  useEffect(() => { load(); }, [load]);

  const submitReview = async (e) => {
    e.preventDefault();
    if (!user) return toast.error("Yorum yapmak için giriş yapın.");
    try {
      await api.post("/reviews", { business_id: b.id, rating, comment });
      toast.success("Yorumunuz eklendi.");
      setComment(""); setRating(5); load();
    } catch { toast.error("Yorum eklenemedi."); }
  };

  if (!b) return <div className="p-8 text-center text-muted-foreground">Yükleniyor...</div>;

  const catObjs = (b.category_ids || []).map((cid) => cats.find((c) => c.id === cid)).filter(Boolean);
  const catNames = catObjs.map((c) => c.name);
  const primaryCat = catObjs[0];
  const PrimaryIcon = primaryCat ? (Icons[primaryCat.icon] || Icons.Truck) : Icons.Truck;
  const hasPublicPhone = !!b.public_virtual_phone;
  const isOpen = b.is_24_7 || b.is_open_now;

  return (
    <div>
      <div className="relative h-64 sm:h-80 overflow-hidden bg-[hsl(var(--brand-black))]">
        <div className="absolute inset-0 bg-gradient-to-br from-[hsl(var(--brand-black))] via-[#171717] to-[#0a0a0a]" />
        <div className="absolute inset-0 opacity-[0.08]" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #FFC107 0px, transparent 140px), radial-gradient(circle at 80% 70%, #FFC107 0px, transparent 200px)" }} />
        <Link to="/" className="absolute top-4 left-4 backdrop-blur-md bg-white/20 hover:bg-white/30 text-white rounded-full p-2 z-10" data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div className="absolute top-4 right-4 flex items-center gap-1 text-primary font-bold text-[10px] sm:text-xs tracking-widest z-10">
          <ShieldCheck className="w-3.5 h-3.5" /> YOL HATTI ONAYLI
        </div>
        <div className="absolute inset-0 flex items-center px-4 sm:px-8">
          <div className="w-14 h-14 sm:w-20 sm:h-20 rounded-2xl bg-primary/15 border border-primary/40 flex items-center justify-center mr-4 shrink-0">
            <PrimaryIcon className="w-8 h-8 sm:w-12 sm:h-12 text-primary" />
          </div>
          <div className="text-white min-w-0">
            <div className="flex flex-wrap gap-1 mb-2">
              {b.is_demo && <Badge variant="secondary" className="bg-white/95 text-slate-900">DEMO VERİ</Badge>}
              {b.is_24_7 && <Badge className="bg-primary text-[hsl(var(--brand-black))] font-bold">7/24</Badge>}
              {isOpen ? <Badge className="bg-[hsl(var(--success))] text-white">Açık</Badge> : <Badge variant="destructive">Kapalı</Badge>}
            </div>
            <h1 className="text-2xl sm:text-4xl font-extrabold tracking-tight truncate">{b.name}</h1>
            <div className="flex items-center gap-3 mt-1 text-sm text-white/80 flex-wrap">
              <span className="flex items-center gap-1"><Star className="w-4 h-4 fill-primary text-primary" /> {(b.rating_avg || 0).toFixed(1)} ({b.review_count || 0})</span>
              <span className="flex items-center gap-1"><MapPin className="w-4 h-4" /> {b.district}, {b.province}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-4xl px-4 py-6 pb-28">
        {catNames.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-4">
            {catNames.map((n) => <Badge key={n} variant="secondary" className="text-xs">{n}</Badge>)}
          </div>
        )}

        {b.description && (
          <section className="mb-6">
            <h2 className="font-semibold mb-2">Hakkında</h2>
            <p className="text-muted-foreground">{b.description}</p>
          </section>
        )}

        <section className="mb-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="rounded-2xl border border-border p-4">
            <div className="text-xs uppercase font-semibold text-muted-foreground tracking-wider mb-1">Merkez Adres</div>
            <p className="text-sm">{b.address || `${b.neighborhood ? b.neighborhood + ", " : ""}${b.district} / ${b.province}`}</p>
          </div>
          <div className="rounded-2xl border border-border p-4">
            <div className="text-xs uppercase font-semibold text-muted-foreground tracking-wider mb-1">İletişim</div>
            {hasPublicPhone ? (
              <a href={`tel:${b.public_virtual_phone}`} data-testid="detail-public-phone" className="text-sm font-medium text-primary flex items-center gap-1">
                <Phone className="w-4 h-4" /> {b.public_virtual_phone}
              </a>
            ) : (
              <div data-testid="phone-soon-msg" className="text-sm inline-flex items-center gap-2 text-[hsl(var(--brand-black))]">
                <Info className="w-4 h-4 text-primary" /> Yol Hattı iletişim sistemi yakında aktif
              </div>
            )}
            <div className="text-[11px] text-muted-foreground mt-2 flex items-start gap-1">
              <ShieldCheck className="w-3.5 h-3.5 mt-0.5 text-primary shrink-0" />
              <span>YOL HATTI güvenli hattı üzerinden iletişim kurun. Firma bilgileri platform güvencesi altındadır.</span>
            </div>
          </div>
        </section>

        {(b.service_provinces?.length > 0 || b.service_districts?.length > 0) && (
          <section className="mb-6">
            <h2 className="font-semibold mb-2">Hizmet Bölgeleri</h2>
            <div className="flex flex-wrap gap-1.5">
              {(b.service_provinces || []).map((p) => <Badge key={p} className="bg-primary/15 text-[hsl(var(--brand-black))] hover:bg-primary/15">{p}</Badge>)}
              {(b.service_districts || []).map((d) => <Badge key={d} variant="secondary">{d}</Badge>)}
            </div>
          </section>
        )}

        {vehicles.length > 0 && (
          <section className="mb-6">
            <h2 className="font-semibold mb-2 flex items-center gap-2"><Truck className="w-4 h-4" /> Araç Filosu</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {vehicles.map((v) => (
                <div key={v.id} className="rounded-xl border border-border p-3">
                  <div className="text-sm font-semibold">{v.name}</div>
                  <div className="text-xs text-muted-foreground">{v.vehicle_type}</div>
                  {v.capacity && <div className="text-xs mt-1">Kapasite: {v.capacity}</div>}
                </div>
              ))}
            </div>
          </section>
        )}

        {b.hours && Object.keys(b.hours).length > 0 && !b.is_24_7 && (
          <section className="mb-6">
            <h2 className="font-semibold mb-2 flex items-center gap-2"><Clock className="w-4 h-4" /> Çalışma Saatleri</h2>
            <div className="rounded-2xl border border-border overflow-hidden">
              {DAYS.map((d, i) => {
                const h = b.hours[String(i)];
                return (
                  <div key={i} className="flex justify-between items-center px-4 py-2 border-b border-border last:border-b-0 text-sm">
                    <span className="font-medium">{d}</span>
                    <span className="text-muted-foreground">{h?.closed || !h ? "Kapalı" : `${h.open} - ${h.close}`}</span>
                  </div>
                );
              })}
            </div>
          </section>
        )}
        {b.is_24_7 && (
          <section className="mb-6">
            <div className="rounded-2xl bg-primary/15 border border-primary/40 p-4 flex items-center gap-3">
              <Clock className="w-5 h-5 text-[hsl(var(--brand-black))]" />
              <div>
                <div className="font-bold">7 gün 24 saat</div>
                <div className="text-xs text-muted-foreground">Bu hizmet sağlayıcı kesintisiz hizmet vermektedir.</div>
              </div>
            </div>
          </section>
        )}

        {b.latitude && b.longitude && (
          <section className="mb-6">
            <h2 className="font-semibold mb-2">Konum</h2>
            <div className="rounded-2xl overflow-hidden border border-border h-64">
              <MapView center={[b.latitude, b.longitude]} zoom={14} markers={[{ id: b.id, latitude: b.latitude, longitude: b.longitude, label: b.name }]} className="h-full" />
            </div>
          </section>
        )}
        {(!b.latitude || !b.longitude) && (
          <section className="mb-6 rounded-2xl border border-dashed p-4 text-sm text-muted-foreground">Firma konumu tanımlanmamış.</section>
        )}

        <section className="mb-8">
          <h2 className="font-semibold mb-3">Yorumlar</h2>
          {user && (
            <form onSubmit={submitReview} className="rounded-2xl border border-border p-4 mb-4">
              <div className="flex items-center gap-1 mb-2">
                {[1,2,3,4,5].map((n) => (
                  <button type="button" key={n} onClick={() => setRating(n)} data-testid={`star-${n}`}>
                    <Star className={`w-6 h-6 ${n <= rating ? "fill-primary text-primary" : "text-muted-foreground"}`} />
                  </button>
                ))}
              </div>
              <Textarea data-testid="review-comment" value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Deneyiminizi paylaşın..." rows={3} />
              <Button data-testid="submit-review" type="submit" className="mt-2 rounded-full">Yorum Yap</Button>
            </form>
          )}
          {!user && <p className="text-sm text-muted-foreground mb-4"><Link to="/giris" className="text-primary underline">Giriş yaparak</Link> yorum yapabilirsiniz.</p>}
          <div className="space-y-3">
            {reviews.length === 0 && <p className="text-sm text-muted-foreground">Henüz yorum yok.</p>}
            {reviews.map((r) => (
              <div key={r.id} className="rounded-2xl border border-border p-4">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium">{r.user_name || "Anonim"}</span>
                  <div className="flex items-center gap-0.5 text-primary">
                    {Array.from({ length: r.rating }).map((_, i) => <Star key={i} className="w-4 h-4 fill-current" />)}
                  </div>
                </div>
                <p className="text-sm text-muted-foreground">{r.comment}</p>
              </div>
            ))}
          </div>
        </section>
      </div>

      <div className="fixed bottom-0 inset-x-0 z-30 backdrop-blur-xl bg-background/95 border-t border-border p-3">
        <div className="mx-auto max-w-4xl grid grid-cols-2 gap-2">
          {hasPublicPhone ? (
            <Button asChild className="rounded-full press bg-[hsl(var(--brand-black))] text-white hover:bg-[hsl(var(--brand-black))]/90" data-testid="cta-call">
              <a href={`tel:${b.public_virtual_phone}`}><Phone className="w-4 h-4 mr-1" /> Ara</a>
            </Button>
          ) : (
            <Button disabled className="rounded-full press" variant="outline" data-testid="cta-call-soon">
              <Info className="w-4 h-4 mr-1" /> Yol Hattı yakında aktif
            </Button>
          )}
          <Button asChild className="rounded-full press bg-primary text-[hsl(var(--brand-black))] hover:bg-primary/90 font-semibold" data-testid="cta-dir">
            <a href={`https://www.google.com/maps/dir/?api=1&destination=${b.latitude},${b.longitude}`} target="_blank" rel="noopener noreferrer">
              <Navigation className="w-4 h-4 mr-1" /> Yol Tarifi
            </a>
          </Button>
        </div>
      </div>
    </div>
  );
}
